import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { FlowType } from 'src/app/perical/models/flow.model';

export interface StandardFluidFlowForm {
  flowRate: number;
  type: FlowType;
  pipeRoughness: number;
}

@Component({
    selector: 'app-standard-fluid',
    templateUrl: './standard-fluid.component.html',
    styles: [``],
    providers: subformComponentProviders(StandardFluidComponent),
    standalone: false
})
export class StandardFluidComponent implements OnInit, AfterViewInit {
  public control(name: string): UntypedFormControl {
    return this.form.formGroup.get(name) as UntypedFormControl;
  }
  
  @Input() public operationType: any;

  @Input()
  public userUnits: UserUnitsModel;
  
  public flowUnit: string;
  public flowRateValidation: { min: number, max: number };
  public pipeRoughnessValidation: { min: number, max: number };

  private _defaultPipeRoughness: number;
  private _dataBackup: any | null = null;

  ngOnInit(): void {
    this._defaultPipeRoughness = 0.001968;
    if (this.userUnits.shortLengths == 'in') {
      this.pipeRoughnessValidation = { min: 0, max: 0.25 };
    } else if (this.userUnits.shortLengths == 'cm') {
      this._defaultPipeRoughness = 0.00499;
      this.pipeRoughnessValidation = { min: 0, max: 0.6 };
    } else {
      this._defaultPipeRoughness = 0.0499;
      this.pipeRoughnessValidation = { min: 0, max: 6.3 };
    }
    this.form.formGroup.controls.pipeRoughness.setValidators([Validators.min(this.pipeRoughnessValidation.min), Validators.max(this.pipeRoughnessValidation.max), Validators.required]);

    this.flowUnit = unitsLib[this.userUnits.standardFluidFlow].symbol;
    switch (this.flowUnit) {
      case 'gpm':
        this.flowRateValidation = { min: 0, max: 40000 };
        break;
      case 'bpm':
        this.flowRateValidation = { min: 0, max: 952 };
        break;
      case 'L/min':
        this.flowRateValidation = { min: 0, max: 151416 };
        break;
      case 'm³/min':
        this.flowRateValidation = { min: 0, max: 151 };
        break;
      case 'bbl/d':
        this.flowRateValidation = { min: 0, max: 1371428.6 };
        break;
    }
    this.form.formGroup.controls.flowRate.setValidators([Validators.min(this.flowRateValidation.min), Validators.max(this.flowRateValidation.max), Validators.required]);
  }
  
  ngAfterViewInit(): void {    
    if (this.form.formGroup.controls.pipeRoughness.value == null) {
      this.form.formGroup.controls.pipeRoughness.patchValue(this._defaultPipeRoughness, { emitEvent: false });
    }
    this._dataBackup = this.form.formGroup.value;
  }

public form = createForm<StandardFluidFlowForm>(this, {
  formType: FormType.SUB,
  formControls: {
    flowRate: new UntypedFormControl(null, { updateOn: 'blur' }),
    type: new UntypedFormControl(FlowType.STANDARD),
    pipeRoughness: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] })
  },
  fromFormGroup: (formValue: StandardFluidFlowForm) => {
    const controls = this.form.formGroup.controls;
    const check = (control, field) => control?.invalid || (control?.touched && this._dataBackup?.[field] === formValue[field]);

    if (check(controls.flowRate, 'flowRate') || 
        check(controls.pipeRoughness, 'pipeRoughness')) {
      
      controls.flowRate.markAsUntouched();
      controls.pipeRoughness.markAsUntouched();
      return;
    }

    this._dataBackup = formValue;
    return this.form.formGroup.getRawValue();
  }
});
}
